import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ActionSheetController, Platform } from '@ionic/angular';
import { AuditNodeViewModel } from 'src/app/business/swodoc/view-models/audit-node-view-model';
import { Logger } from 'src/app/common/logging/logger';
import { v4 } from 'uuid';

import { Capacitor } from '@capacitor/core';
import { TranslateService } from '@ngx-translate/core';
import { ATTACHMENT_LOCAL_PREFIX } from '@services/attachment/attachment-service.interface';
import { CameraImageService } from 'src/app/shared/services/camera-image.service';
import { DocumentScannerService } from 'src/app/shared/services/document-scanner.service';
import { IAttachmentWithData } from '../../document-attachment.service';
import { DocumentEditService } from '../../document-edit.service';

@Component({
  selector: 'swodoc-node-document-scan-ios',
  templateUrl: 'swodoc-node-document-scan-ios.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SwodocNodeDocumentScanIOS implements OnInit {
  @Input() node: AuditNodeViewModel;
  @Input() documentEditService: DocumentEditService;
  @Output() changed = new EventEmitter<AuditNodeViewModel>();

  @ViewChild('file', { static: true })
  public fileInputElementRef: ElementRef;

  public images: Partial<IAttachmentWithData>[];
  @ViewChild('markerImageElement', { static: true })
  public markerImageElementRef: ElementRef;
  public markerImage: string;

  constructor(
    private _changeDetectionRef: ChangeDetectorRef,
    private _platform: Platform,
    private _logger: Logger,
    private _actionSheetCtrl: ActionSheetController,
    private _documentScannerService: DocumentScannerService,
    private _imageService: CameraImageService,
    private _translate: TranslateService
  ) {}

  ngOnInit() {
    this.images = this.documentEditService.getImagesForNodeId(this.node.id);
  }

  public async addImage(): Promise<void> {
    if (!this._platform.is('hybrid')) {
      this.fileInputElementRef.nativeElement.click();
    } else {
      const addFromScanner = async () => {
        try {
          const swodocImagePath = await this._documentScannerService.scanOneImageFromCameraAndResize();
          if (!swodocImagePath) {
            throw new Error(`Camera scan and / or resize of image failed.`);
          }
          this.addImagePathToImagesArray(swodocImagePath);
        } catch (e) {
          this._logger.captureErrorWithExtras(
            'Document scanner failed',
            e,
            new Map([['component', 'swodoc-node-document-scan-ios']]),
            'warning'
          );
          console.error(e);
        }
      };
      const addFromLibrary = () => {
        this._imageService
          .getPictureFromGallery()
          .then(image => this.addImageToImagesArray(image))
          .catch(err => {
            this._logger.captureErrorWithExtras(
              'GetPicture failed on device',
              err,
              new Map([['component', 'swodoc-node-document-scan-ios']]),
              'warning'
            );
            console.error(err);
          });
      };

      const actionSheet = await this._actionSheetCtrl.create({
        buttons: [
          {
            text: this._translate.instant('audit.node.image.select_image_actionsheet.camera'),
            handler: addFromScanner,
          },
          {
            text: this._translate.instant('audit.node.image.select_image_actionsheet.fotolibrary'),
            handler: addFromLibrary,
          },
          {
            text: this._translate.instant('audit.node.image.select_image_actionsheet.cancel'),
            handler: () => {},
          },
        ],
      });
      await actionSheet.present();
    }
  }

  public handleFileOpenDialog(event: Event) {
    const file = (event.target as any).files[0];
    const fileReader = new FileReader();
    fileReader.readAsDataURL(file);
    fileReader.onloadend = () => {
      this.addImageToImagesArray(fileReader.result.toString());
    };

    // Clear the filename input in order to allow upload of the same file multiple times.
    // Otherwise the change event would not fire
    (event.target as any).value = null;
  }

  private addImageToImagesArray(image) {
    const newImage = {
      _id: `${ATTACHMENT_LOCAL_PREFIX}${v4()}`,
      metadata: {
        auditNodeId: this.node.id,
        type: 100, // SwodocImage
      },
      data: image,
    } as Partial<IAttachmentWithData>;

    this.documentEditService.addImage(newImage);
    this.images.push(newImage);
    this._changeDetectionRef.markForCheck();
  }
  private addImagePathToImagesArray(imagePath: string) {
    const newImage = {
      _id: `${ATTACHMENT_LOCAL_PREFIX}${v4()}`,
      metadata: {
        auditNodeId: this.node.id,
        type: 100, // SwodocImage
      },
      dataPath: imagePath,
    } as Partial<IAttachmentWithData>;

    this.documentEditService.addImage(newImage);
    this.images.push(newImage);
    this._changeDetectionRef.markForCheck();
  }
  public convertDataPathToFileSrc(dataPath: string) {
    return Capacitor.convertFileSrc(dataPath);
  }
}
